import {useOrganizationService} from "../../../../services";
import {useJwt} from "../../../../helpers/common";
import React, {ChangeEvent, useEffect, useState, useMemo} from "react";
import {useSelector} from 'react-redux';
import {HiLink} from "react-icons/hi";
import toast from 'react-hot-toast';
import CustomButton from '../../../../components/common/CustomButton';
import FormInput from '../../../../components/common/FormInput';
import ToggleSwitch from "../../../../components/common/ToggleSwitch";
import useIntegrationStatus from "../../../../hooks/useIntegrationStatus";
import MondaySettingsSkeleton from "./MondaySettingsSkeleton";
import UnsavedChangesIndicator from "../../../../components/common/UnsavedChangesIndicator";

interface MondaySettingsProps {
  handleClose: () => void;
  handleActivationCallback?: () => void;
}

const MondaySettings: React.FC<MondaySettingsProps> = ({ handleClose, handleActivationCallback }) => {
  const jwtData: any= useJwt();
  const org_id = jwtData.org_id;
  const { saveMondaySettings, getMondaySettings } = useOrganizationService();
  const { isAnyOtherIntegrationActive } = useIntegrationStatus();

  const [payload, setPayload] = useState({
    api_token: undefined,
    status_stages: '',
    is_active: false
  });

  const {
    mondaySettingsLoading,
    mondaySettings
  } = useSelector((state: any) => state.organization);

  const handleOnChange= (e: ChangeEvent<HTMLInputElement | HTMLTextAreaElement>)=> {
    const { name, value } = e.target;
    setPayload({...payload,[name]:value})
  }

  const handleCheckbox= (e: ChangeEvent<HTMLInputElement>)=> {
    const {checked} = e.target;

    if(checked) {
      setPayload({...payload,is_active:true})
    } else {
      setPayload({...payload,is_active:false})
    }
  }

  const onSubmit = () => {
    if(!payload?.api_token) {
      toast.error("Please Add API token", {duration:2000});
      return;
    }
    // if(isAnyOtherIntegrationActive("monday")){
    //   toast.error("Only one integration can be active at a time.", {duration:2000});
    //   return;
    // }

    saveMondaySettings(org_id,{...payload})
      .then(() => {
        handleActivationCallback?.();
      })
  }

  useEffect(()=>{
    if(org_id){
      getMondaySettings(org_id)
    }
  },[org_id]);

  useEffect(() => {
    if(!mondaySettingsLoading && mondaySettings) {
      if(!payload.api_token) {
        setPayload(mondaySettings)
      }
    }
  }, [mondaySettingsLoading]);

  const hasUnsavedChanges = useMemo(() => {
    if (!mondaySettings || mondaySettingsLoading) {
      return false;
    }
    const normalizeString = (val: any) => (val === null || val === undefined ? '' : String(val).trim());
    const normalizeBool = (val: any) => Boolean(val);

    return (
      normalizeString(payload?.api_token) !== normalizeString(mondaySettings?.api_token) ||
      normalizeString(payload?.status_stages) !== normalizeString(mondaySettings?.status_stages) ||
      normalizeBool(payload?.is_active) !== normalizeBool(mondaySettings?.is_active)
    );
  }, [payload, mondaySettings, mondaySettingsLoading]);

  return (
    <div className="row bg-tertiary-light dark:bg-tertiary-dark w-full h-full relative flex flex-col">
      {/* Header */}
      <div className="flex flex-col border-b bg-light-surfacePrimary border-gray-500 dark:border-gray-700 !p-4 !pl-6 !pt-5
      dark:bg-tertiary-dark sticky top-0 z-40 shadow-lg rounded-b-2xl">
        <div className="flex w-full items-start justify-between">
          <div className="flex flex-col min-w-0 max-w-full">
            <div className="text-light-text-primary dark:text-white text-lg font-semibold font-['Inter'] leading-tight truncate pr-2">
              Monday Settings
            </div>
            <a
              href="https://cadence-fe-bucket.s3.us-east-2.amazonaws.com/documents/CadenceMondaySetupGuide.pdf"
              target="_blank"
              rel="noreferrer"
              className="flex flex-row items-center gap-2 mt-2 text-light-text-tertiary dark:text-white/60 text-sm hover:text-sky-500 dark:hover:text-sky-200"
            >
              <HiLink className="w-4 h-4"/>
              Visit Cadence-Monday Setup guide
            </a>
          </div>
          <div className="flex items-center gap-2">
            <button
              type="button"
              className="text-light-text-secondary hover:text-light-text-primary dark:text-white/70 dark:hover:text-white
              hover:bg-light-interactive-hover dark:hover:bg-white/10 p-2 rounded-lg transition-colors duration-200"
              onClick={handleClose}>
              <svg className="w-5 h-5" fill="currentColor" viewBox="0 0 20 20" xmlns="http://www.w3.org/2000/svg">
                <path fillRule="evenodd" d="M4.293 4.293a1 1 0 011.414 0L10 8.586l4.293-4.293a1 1 0 111.414 1.414L11.414 10l4.293 4.293a1 1 0 01-1.414 1.414L10 11.414l-4.293 4.293a1 1 0 01-1.414-1.414L8.586 10 4.293 5.707a1 1 0 010-1.414z" clipRule="evenodd"></path>
              </svg>
            </button>
          </div>
        </div>
      </div>

      {mondaySettingsLoading ? (
        <MondaySettingsSkeleton />
      ) : (
        <div className="px-8 py-12 flex-1">
          <FormInput
            id="api_token"
            name="api_token"
            label={
              <span className="flex">
                API Token
                <span className="ml-1">*</span>
              </span>
            }
            type="password"
            value={payload?.api_token || ''}
            onChange={handleOnChange}
            placeholder="Enter api token"
          />
          <FormInput
            id="status_stages"
            name="status_stages"
            label="States"
            type="textarea"
            value={payload?.status_stages || ''}
            onChange={handleOnChange}
            placeholder='{
              "In QA": "25%",
              "QA Tested": "50%",
              "In Prod": "100%"
            }'
            className="w-full h-40 block p-2 disabled:pointer-events-none text-gray-900 border border-gray-300 bg-gray-50 sm:text-md focus:ring-blue-500 focus:border-blue-500 dark:bg-gray-700 dark:border-gray-600 dark:placeholder-gray-400 dark:text-white dark:focus:ring-blue-500 dark:focus:border-blue-500"
            maxLength={1000}
          />
          <ToggleSwitch
            id="is_active"
            name="is_active"
            checked={payload?.is_active}
            onChange={handleCheckbox}
            label="Activate Monday Integration in Cadence Pro"
          />
        </div>
      )}

      {/* Footer */}
      <div className="px-4 py-4 items-center justify-between flex gap-4 mt-auto border-t rounded-t-md border-gray-500 dark:border-gray-700
        bg-light-surfacePrimary dark:bg-tertiary-dark shadow-lg sticky bottom-0">
        <CustomButton
          variant="cancel"
          onClick={handleClose}
          className="ml-2"
        >
          Cancel
        </CustomButton>

        <div className="ml-auto flex items-center gap-3">
          <UnsavedChangesIndicator hasUnsavedChanges={hasUnsavedChanges} />
          <CustomButton
            onClick={onSubmit}
            disabled={
              mondaySettingsLoading
              || !payload.api_token
            }
            title={isAnyOtherIntegrationActive('monday') ? "Only one integration can be active at a time." : ""}
            isLoading={mondaySettingsLoading}
            size="lg"
          >
            {!mondaySettingsLoading ? 'Save' : "Saving..."}
          </CustomButton>
        </div>
      </div>
    </div>
  )
}

export default MondaySettings;